import { TagChip } from "./Pills";
import { ErrorBanner, LoadingNote } from "./Layout";
import { PagedFeed } from "../hooks/usePagedFeed";
import { colors, type } from "../styles";
import { formatAgo, formatDateTime } from "../format";
import { DmFlagRow, DmFlagType } from "../types";

const FLAG_LABELS: Record<DmFlagType, string> = {
  SEND_FAILURE: "Send failure",
  DUPLICATE_SEND: "Duplicate send",
  REPROMPT_LOOP: "Re-prompt loop",
  BOT_AFTER_CREATOR: "Bot after creator",
};

const handle = (username: string | undefined, fallback: string | undefined) =>
  username ? `@${username}` : fallback ?? "—";

/**
 * The most recent anomaly flags across every creator. A row opens the thread it
 * was raised on; a send failure with no fan on it (a private reply to a comment
 * that never reached an inbox) opens just the account.
 */
const DmFlagsList = ({
  feed,
  onSelect,
}: {
  feed: PagedFeed<DmFlagRow>;
  onSelect: (flag: DmFlagRow) => void;
}) => (
  <div style={{ display: "flex", flexDirection: "column", minHeight: 0, flex: 1 }}>
    {feed.error && (
      <div style={{ padding: "14px 14px 0" }}>
        <ErrorBanner message={feed.error} />
      </div>
    )}
    {feed.loading && (
      <div style={{ padding: "0 14px" }}>
        <LoadingNote />
      </div>
    )}
    {!feed.loading && !feed.error && feed.items.length === 0 && (
      <div style={{ ...type.bodyRegular, color: colors.inkMuted, padding: 24 }}>
        No flags yet. Anything detected in a DM thread shows up here.
      </div>
    )}
    <div style={{ overflowY: "auto", flex: 1, minHeight: 0 }}>
      {feed.items.map(flag => (
        <button
          key={flag.id}
          onClick={() => onSelect(flag)}
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 16,
            width: "100%",
            textAlign: "left",
            background: "transparent",
            border: "none",
            borderBottom: `1px solid ${colors.border}`,
            padding: "12px 16px",
            cursor: "pointer",
          }}
        >
          <div style={{ width: 150, flexShrink: 0 }}>
            <TagChip
              label={FLAG_LABELS[flag.type] ?? flag.type}
              tone={flag.type === "SEND_FAILURE" ? "critical" : "warning"}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ ...type.bodyRegular, color: colors.ink, overflowWrap: "anywhere" }}>
              {flag.details}
            </div>
            <div
              style={{
                ...type.bodySmallRegular,
                color: colors.inkMuted,
                marginTop: 3,
                display: "flex",
                flexWrap: "wrap",
                gap: "2px 12px",
              }}
            >
              <span>Creator {handle(flag.creatorIgUsername, flag.creatorUid)}</span>
              {(flag.fanIgUsername || flag.fanIgUserId) && (
                <span>Fan {handle(flag.fanIgUsername, flag.fanIgUserId)}</span>
              )}
              {flag.attempts !== undefined && <span>{flag.attempts} attempts</span>}
              {flag.final && <span>Final attempt</span>}
              {flag.recovered && <span>Recovered on retry</span>}
            </div>
          </div>
          <div
            style={{
              ...type.bodySmallRegular,
              color: colors.inkFaint,
              flexShrink: 0,
              textAlign: "right",
            }}
            title={formatDateTime(flag.createdAt)}
          >
            {formatAgo(flag.createdAt)}
          </div>
        </button>
      ))}
      {feed.hasMore && (
        <div style={{ padding: 14, textAlign: "center" }}>
          <button
            onClick={feed.loadMore}
            disabled={feed.loadingMore}
            style={{
              ...type.bodySmallMedium,
              color: colors.inkMuted,
              background: colors.surface,
              border: `1px solid ${colors.border}`,
              borderRadius: 6,
              padding: "6px 12px",
              cursor: feed.loadingMore ? "default" : "pointer",
            }}
          >
            {feed.loadingMore ? "Loading…" : "Load more flags"}
          </button>
        </div>
      )}
    </div>
  </div>
);

export default DmFlagsList;
